import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Divider } from "@mui/material";

import PungoDemoAccordion from "pungo-ui/PungoDemoAccordion";
import PungoSectionCard from "pungo-ui/PungoSectionCard";
import PungoDemoResultsCard from "pungo-ui/PungoDemoResultsCard/index";
import cost from "assets/AucaImages/Cost.svg";
import optimizedCost from "assets/AucaImages/OptimizedCost.svg";
import carbonEmissions from "assets/AucaImages/carbonEmissions.svg";
import reducedCarbonEmissions from "assets/AucaImages/reducedCarbonEmissions.svg";
import energy from "assets/AucaImages/Energy.svg";
import energyOptimized from "assets/AucaImages/EnergyOptimized.svg";
import PungoTable from "pungo-ui/PungoTable";
import DemoResultsDoughnut from "pungo-ui/PungoResultsDoughnut";
import PungoOptionSelectionByButton from "pungo-ui/PungoOptionSelectionByButton";
import { selectResults } from "store/slices/plan";

import colors from "styles/export.module.scss";
import styles from "./index.module.scss";

const ResultsSection: React.FC = () => {
  const { t, i18n } = useTranslation();

  const {
    totalEmissions,
    totalEnergyCost,
    totalEnergyContent,
    energySourceResults,
    emissionsIntensity,
    emissionsIntensityUnit,
    energyIntensity,
    energyIntensityUnit,
    costIntensity,
    costIntensityUnit,
    emissionsToReduce,
    optimisedCosts,
    optimisedEmissions,
    emissionsToReduceTreeEquivalence,
    costToReduce,
    energyContentToReduce,
    optimisedEnergyContent,
  } = useSelector(selectResults);

  const [chartType, setChartType] = useState("energy");
  const [selection01, setSelection01] = useState(0);

  const isSpanishLanguageSelected = i18n.language === "es";

  const getTreeEquivalenceMessage = () => {
    return (
      <div className={styles.treeEquivalenceMessage}>
        {t("DemoPage.ResultsSection.emissionReduction")}
        <span className={styles.accents}>
          &nbsp;{emissionsToReduceTreeEquivalence}&nbsp;
          {t("DemoPage.ResultsSection.trees")}
        </span>
      </div>
    );
  };

  const getEmptyState = () => (
    <>
      <div className={styles.emptyState}>{t("DemoPage.ResultsSection.emptyState")}</div>
    </>
  );

  const handleOptionSelect01 = (index: number, name: string) => {
    setSelection01(index);
  };

  const handleOptionSelect02 = (index: number, name: string) => {
    console.log (index, name);
    if (index === 0) {
      setChartType("energy");
    } else if (index === 1) {
      setChartType("emissions");
    } else if (index === 2){
      setChartType("costs");
    } else{
      setChartType("energy");
    }
  };

  const option01List = [`${t("DemoPage.ResultsSection.energy")}`, `${t("DemoPage.ResultsSection.emissions")}`, `${t("DemoPage.ResultsSection.costs")}`];
  const option02List = [
    `${t("DemoPage.ResultsSection.energyConsumptionContributionMenu")}`,
    `${t("DemoPage.ResultsSection.emissionsContributionMenu")}`,
    `${t("DemoPage.ResultsSection.energyCostContributionMenu")}`,
  ];

  const labelsForTable = [
    t("DemoPage.ResultsSection.energySources"),
    t("DemoPage.ResultsSection.emissionsKg"),
    t("DemoPage.ResultsSection.emissionsScope"),
    t("DemoPage.ResultsSection.energyCost"),
    t("DemoPage.ResultsSection.energyContent"),
  ];

  const valuesForTable = energySourceResults.map(({ emission, emissionScopeEs, emissionScopeEn, energyCost, energyContent, nameEs, nameEn }) =>
    isSpanishLanguageSelected ? [nameEs, emission, emissionScopeEs, energyCost, energyContent] : [nameEn, emission, emissionScopeEn, energyCost, energyContent]
  );

  const getTotalCostsContent = () => (
    <>
      <div>
        <div className={styles.results}>
          <div className={styles.resultCard}>
            <PungoDemoResultsCard description={t("DemoPage.ResultsSection.totalEnergyContent")} result={`${totalEnergyContent.toFixed(0)} kWh`} color={colors.red1} />
          </div>
          <div className={styles.resultCard}>
            <PungoDemoResultsCard description={t("DemoPage.ResultsSection.totalCarbonEmissions")} result={`${totalEmissions.toFixed(0)} kg CO2-eq`} color={colors.red1} />
          </div>
          <div className={styles.resultCard}>
            <PungoDemoResultsCard description={t("DemoPage.ResultsSection.totalEnergyCost")} result={`${totalEnergyCost.toFixed(0)} USD`} color={colors.red1} />
          </div>
        </div>
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <div className={styles.tableContainer}>
            <PungoTable values={valuesForTable} labels={labelsForTable} height={450} />
          </div>
        </div>

        <PungoOptionSelectionByButton names={option02List} onSelect={handleOptionSelect02} minWidth={240} />
        <Divider className={styles.divider} />

        <div className={styles.chartContainer}>
          <div className={styles.chart}>
            <DemoResultsDoughnut resultsData={energySourceResults} chartType={chartType} />
          </div>
        </div>
      </div>
    </>
  );
  const getOptimizationContent = () => (
    <>
      <div>
        <div className={styles.text}>{t("results.optimization")}</div>

        <PungoOptionSelectionByButton names={option01List} onSelect={handleOptionSelect01} minWidth={90} />
        <Divider className={styles.divider} />

        {selection01 === 2 && (
          <div className={styles.results}>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard description={t("DemoPage.OptimizationSection.currentCosts")} result={`${totalEnergyCost.toFixed(0)} USD`} color={colors.red1} image={cost} />
            </div>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard
                description={t("DemoPage.OptimizationSection.optimizedCosts")}
                result={`${optimisedCosts.toFixed(0)} USD`}
                color={colors.green1}
                image={optimizedCost}
                reducedCost={`${costToReduce.toFixed(0)} USD`}
              />
            </div>
          </div>
        )}

        {selection01 === 1 && (
          <div className={styles.results}>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard
                description={t("DemoPage.OptimizationSection.currentCarbonEmissions")}
                result={`${totalEmissions.toFixed(0)} kg CO2-eq`}
                color={colors.red1}
                image={carbonEmissions}
              />
            </div>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard
                description={t("DemoPage.OptimizationSection.optimizedCarbonEmissions")}
                result={`${optimisedEmissions.toFixed(0)} kg CO2-eq`}
                color={colors.green1}
                image={reducedCarbonEmissions}
                reducedEmissions={`${emissionsToReduce} kg CO2-eq`}
              />
            </div>
          </div>
        )}

        {selection01 === 0 && (
          <div className={styles.results}>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard description={t("DemoPage.OptimizationSection.currentEnergy")} result={`${totalEnergyContent.toFixed(0)} kWh`} color={colors.red1} image={energy} />
            </div>
            <div className={styles.resultCard}>
              <PungoDemoResultsCard
                description={t("DemoPage.OptimizationSection.optimizedEnergy")}
                result={`${optimisedEnergyContent.toFixed(0)} kWh`}
                color={colors.green1}
                image={energyOptimized}
                reducedEnergy={`${energyContentToReduce.toFixed(0)} kWh`}
              />
            </div>
          </div>
        )}

        <div className={styles.resultMessage}>{getTreeEquivalenceMessage()}</div>
        {/* <div className={styles.resultMessage}>{getResultMesssage()}</div> */}
      </div>
    </>
  );
  const getIndicatorsContent = () => (
    <>
      <div className={styles.results}>
        <div className={styles.resultCard}>
          <PungoDemoResultsCard description={t("DemoPage.ResultsSection.energyIntensity")} result={`${energyIntensity.toFixed(2)} ${energyIntensityUnit}`} color={colors.red1} />
        </div>
        <div className={styles.resultCard}>
          <PungoDemoResultsCard description={t("DemoPage.ResultsSection.emissionsIntensity")} result={`${emissionsIntensity.toFixed(2)} ${emissionsIntensityUnit}`} color={colors.red1} />
        </div>
        <div className={styles.resultCard}>
          <PungoDemoResultsCard description={t("DemoPage.ResultsSection.costIntensity")} result={`${costIntensity.toFixed(2)} ${costIntensityUnit}`} color={colors.red1} />
        </div>
      </div>
    </>
  );
  return (
    <PungoSectionCard title={t("DemoPage.ResultsSection.results")} padding="no">
      {!totalEmissions ? (
        getEmptyState()
      ) : (
        <div className={styles.container}>
          <div className={styles.resultsContainer} id="refResultsEmissions">
            <PungoDemoAccordion title={t("DemoPage.ResultsSection.totalCosts")} content={getTotalCostsContent()} />
          </div>
          <br></br>
          <div className={styles.resultsContainer} id="refResultsIndicators">
            <PungoDemoAccordion title={t("DemoPage.ResultsSection.indicatorsPerUnit")} content={getIndicatorsContent()} />
          </div>
          <br></br>
          <div className={styles.resultsContainer} id="refResultsOptimizations">
            <PungoDemoAccordion title={t("DemoPage.OptimizationSection.optimization")} content={getOptimizationContent()} />
          </div>
        </div>
      )}
    </PungoSectionCard>
  );
};

export default ResultsSection;
